const ONE_HOUR_IN_MS = 60 * 60 * 1000;

/** How long a reservation stays resumable after the guest's last action. */
export const RESUME_LIFETIME_MS = ONE_HOUR_IN_MS;

/**
 * The server reprices on every load, so comparing with the total the guest last saw is
 * the only signal that something changed while they were away.
 */
export function hasPriceChanged(remembered: string, current: string | undefined | null): boolean {
	if (remembered === '' || current === undefined || current === null || current === '') return false;

	return remembered !== current;
}

/**
 * One expiry for the whole reservation instead of one per stored value: a clock per value
 * drifts apart, and the id in particular never changes after the flow is created, so it
 * would expire while the guest was still busy. A missing or unreadable expiry counts as
 * expired, which covers both a first visit and leftovers from an older version.
 */
export function createResumableSession(
	storage: Storage,
	scope: string,
	lifetimeMs: number = RESUME_LIFETIME_MS,
	now: () => number = Date.now,
) {
	const expiresAtKey = `${scope}/flow/expires-at`;

	const scopedKeys = (): string[] => {
		const keys: string[] = [];

		for (let index = 0; index < storage.length; index++) {
			const key = storage.key(index);
			if (key?.startsWith(`${scope}/`)) keys.push(key);
		}

		return keys;
	};

	return {
		expiresAtKey,

		hasExpired: (): boolean => {
			// Number rather than parseInt, which reads '123abc' as a valid moment.
			const expiresAt = Number(storage.getItem(expiresAtKey));

			return !Number.isSafeInteger(expiresAt) || expiresAt <= now();
		},

		clear: (): void => {
			scopedKeys().forEach((key) => storage.removeItem(key));
		},

		touch: (): void => {
			storage.setItem(expiresAtKey, String(now() + lifetimeMs));
		},
	};
}
